import React, { useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import RootStore from "SandboxViewerComponent/src/stores/RootStore";
import introJs from "intro.js";
import "intro.js/introjs.css";
import "./Introduction.css";

// ─── Suppress-preference helpers ─────────────────────────────────────────────
// The "don't auto-start" preference is stored as a single localStorage flag.
// All reads and writes go through these two functions so the key name and
// serialization are defined in exactly one place.

const INTRO_SUPPRESS_KEY = "niklis.intro.suppress";

const isIntroDismissed = () =>
  localStorage.getItem(INTRO_SUPPRESS_KEY) === "true";

const setIntroDismissed = (value) => {
  if (value) {
    localStorage.setItem(INTRO_SUPPRESS_KEY, "true");
  } else {
    localStorage.removeItem(INTRO_SUPPRESS_KEY);
  }
};

// ─── "Don't show again" checkbox injector ────────────────────────────────────
// intro.js renders step content via innerHTML, which strips <input> elements
// through its built-in sanitizer. We therefore inject the checkbox via the DOM
// API (createElement) after the HTML has been written.
//
// A MutationObserver on document.body watches for whenever intro.js rewrites
// the tooltip — which happens on every step change including step-back. When it
// detects the step-0 tooltip (identified by a sentinel data attribute), it
// appends (or re-syncs) the checkbox. A data attribute on a <div> survives
// intro.js's HTML sanitizer; a text-content match would break silently if the
// copy ever changes.

// Sentinel attribute placed on a <div> inside the step-0 intro HTML.
// <div> elements with data attributes survive intro.js's HTML sanitizer;
// the sentinel is the reliable hook for locating the step-0 tooltip.
const STEP0_SENTINEL_ATTR = "data-niklis-intro-step";
const STEP0_SENTINEL_VALUE = "0";

/**
 * Append the "don't show again" checkbox to a step-0 tooltip node, or sync
 * the checked state if it was already appended.
 *
 * @param {Element} tooltipTextEl - The .introjs-tooltiptext DOM element.
 */
function injectSuppressCheckbox(tooltipTextEl) {
  // Re-use the existing node if intro.js kept it across renders.
  let cb = tooltipTextEl.querySelector('[data-intro-pref="suppress"]');
  if (cb) {
    cb.checked = isIntroDismissed();
    return;
  }

  // Build the label + checkbox via the DOM API so sanitizers cannot touch it.
  cb = Object.assign(document.createElement("input"), {
    type: "checkbox",
    checked: isIntroDismissed(),
  });
  cb.setAttribute("data-intro-pref", "suppress");
  cb.style.cursor = "pointer";
  cb.onchange = () => setIntroDismissed(cb.checked);

  const label = document.createElement("label");
  label.style.cssText =
    "font-size:13px;cursor:pointer;display:flex;align-items:center;gap:6px";
  label.appendChild(cb);
  label.appendChild(document.createTextNode("Einführung nicht mehr anzeigen"));

  const wrapper = document.createElement("div");
  wrapper.style.marginTop = "12px";
  wrapper.appendChild(label);

  tooltipTextEl.appendChild(wrapper);
}

/**
 * Scan all current .introjs-tooltiptext nodes and inject the checkbox into
 * any that belong to step 0 (detected via its unique marker text).
 * Called by the MutationObserver callback and once at intro start.
 */
function syncSuppressCheckbox() {
  document
    .querySelectorAll(`[${STEP0_SENTINEL_ATTR}="${STEP0_SENTINEL_VALUE}"]`)
    .forEach((sentinel) => {
      const tooltipTextEl = sentinel.closest(".introjs-tooltiptext");
      if (tooltipTextEl) injectSuppressCheckbox(tooltipTextEl);
    });
}

// ─── Module-level active instance tracker ─────────────────────────────────────
// Kept outside the component so it survives React re-renders and can be
// accessed for cleanup without extra state.

const DEFAULT_LAYOUT_SETTLE_FRAMES = 2;
const MOBILE_PANEL_SETTLE_FRAMES = 4;

function waitForAnimationFrames(frameCount) {
  return new Promise((resolve) => {
    const waitForNextFrame = (remainingFrames) => {
      if (remainingFrames <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(() => waitForNextFrame(remainingFrames - 1));
    };

    waitForNextFrame(frameCount);
  });
}

function waitForIntroLayout() {
  const frameCount = RootStore.uiStore?.BBStore?.mobile
    ? MOBILE_PANEL_SETTLE_FRAMES
    : DEFAULT_LAYOUT_SETTLE_FRAMES;

  return waitForAnimationFrames(frameCount);
}

let currentIntroInstance = null;

function scheduleIntroRefresh(instance) {
  waitForIntroLayout().then(() => {
    if (currentIntroInstance !== instance) return;

    try {
      instance.refresh();
      syncSuppressCheckbox();
    } catch (e) {
      console.warn("Introduction: failed to refresh intro.js layout", e);
    }
  });
}

// Each step carries both the intro.js properties (element, intro) and the
// panel-visibility action needed before it is shown. Extra properties are
// silently ignored by intro.js but consumed by the onbeforechange handler.
//
// `requiredPanel`       – panel that must be open; detected via offsetParent.
//                       - If not visible, it is opened before the step is shown.
// `closePanelsOnMobile` – element is in the map/overlay area; on mobile an open
//                         panel covers it even though it is in the DOM.
//                         If true, any open panel is closed before the step is shown.
const INTRO_STEPS = [
  {
    intro: `<div ${STEP0_SENTINEL_ATTR}="${STEP0_SENTINEL_VALUE}">Starten Sie die Einführung in NIKLIS und seine Funktionen</div>`,
  },
  {
    element: "#dropDownFilterParams",
    intro:
      "Wählen Sie zuerst einen Parameter aus, den Sie näher betrachten möchten.",
    requiredPanel: { direction: "left", widgetId: "mapFilter" },
  },
  {
    element: "#klimaSliderTimeline",
    intro:
      "Sie können sich nun für unterschiedliche Zeitschritte die Daten räumlich visualisieren lassen...",
    closePanelsOnMobile: true,
  },
  {
    element: "#klimaSliderFutureControls",
    intro:
      "Auch für zwei Zukunftszeiträume auf Basis  unterschiedlicher Treibhausgas-Szenarien (RCPs) können Sie sich eine mögliche Zukunft mit den entsprechenden Unsicherheiten (Maximum, Mittel, Minimum) ansehen ...",
    closePanelsOnMobile: true,
  },
  {
    element: "#faktenBlatt",
    intro:
      "… oder sich für eine genauere Zeitreihenbetrachtung einer Region, eines Parameters und eines Zeitabschnitts ein Faktenblatt berechnen lassen",
    requiredPanel: { direction: "right", widgetId: "stats" },
  },
];

const STEP_OPTIONS = {
  nextLabel: "Weiter",
  prevLabel: "Zurück",
  skipLabel: "x",
  doneLabel: "Fertig",
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Introduction component.
 *
 * Starts the guided intro.js tour when the map and all targeted DOM elements
 * are ready. Auto-start is skipped when the user has checked "don't show again".
 *
 * On step 0, a "don't show again" checkbox is injected into the tooltip.
 * `window.startIntro` is exposed so any other UI element (e.g. BottomBar) can
 * trigger the intro manually.
 *
 * On step 1 (parameter selection), the first available options for Kategorie,
 * Parameter, and Zeitabschnitt are auto-selected if they are unset.
 */
const Introduction = () => {
  const { mapStore } = RootStore;

  /**
   * Set first available choice for Kategorie, Parameter, Zeitabschnitt
   * when they are unset.
   */
  const selectFirstValues = useCallback(() => {
    const commStore = RootStore.communicationStore;
    const attributeValues = commStore?.stores?.attributeEdit?.attributeValues;
    if (!attributeValues) return;

    const stores = [
      attributeValues["Kategorie"],
      attributeValues["Parameter"],
      attributeValues["Zeitabschnitt"],
    ];

    stores.forEach((store) => {
      if (!store) return;
      const choices = store.updateOptions?.choices;
      if (choices?.length > 0 && !store.value) {
        store.setValue(choices[0].value);
      }
    });
  }, []);

  /**
   * Create and start an introJs instance with proper lifecycle hooks.
   * Exits any previously running instance first to avoid overlaps.
   */
  const startIntroInstance = useCallback(() => {
    try {
      if (currentIntroInstance) {
        try {
          currentIntroInstance.exit();
        } catch (e) {
          console.warn("Introduction: failed to exit previous instance", e);
        }
        currentIntroInstance = null;
      }

      const instance = introJs();
      instance.setOptions({ ...STEP_OPTIONS, steps: INTRO_STEPS });

      // Attach a MutationObserver so the checkbox is (re-)injected each time
      // intro.js rewrites the tooltip HTML — including on step-back.
      const observer = new MutationObserver(syncSuppressCheckbox);
      observer.observe(document.body, { childList: true, subtree: true });
      syncSuppressCheckbox(); // handle the case where the tooltip is already in the DOM

      // Before each step: ensure the target element is visible.
      // Returning a Promise makes intro.js wait before positioning the tooltip.
      instance.onbeforechange((targetElement) => {
        try {
          if (targetElement?.id === "dropDownFilterParams") {
            selectFirstValues();
          }
        } catch (e) {
          console.error("Introduction: error in onbeforechange:", e);
        }

        // Find the step config for this targetElement directly from INTRO_STEPS.
        const stepConfig = INTRO_STEPS.find(
          (step) => step.element && targetElement?.matches(step.element)
        );

        if (!stepConfig) return;

        // ── Case 1: element lives inside a panel that may be closed ──────────
        if (stepConfig.requiredPanel) {
          if (!targetElement || targetElement.offsetParent !== null) return; // already visible
          const { direction, widgetId } = stepConfig.requiredPanel;
          try {
            RootStore.uiStore.subUiStores[direction].openWidget(widgetId);
          } catch (e) {
            console.warn(
              "Introduction: could not open panel for step",
              stepConfig.element,
              e
            );
            return;
          }
          return waitForIntroLayout();
        }

        // ── Case 2: element is in the map/overlay area ────────────────────────
        // On mobile, an open panel covers the whole screen, hiding the overlay.
        // Close any open left/right panel so the map area becomes visible.
        if (
          stepConfig.closePanelsOnMobile &&
          RootStore.uiStore.BBStore.mobile
        ) {
          const leftStore = RootStore.uiStore.subUiStores.left;
          const rightStore = RootStore.uiStore.subUiStores.right;
          if (!leftStore.activeWidgetId && !rightStore.activeWidgetId) return;
          try {
            if (leftStore.activeWidgetId) leftStore.closeWidget();
            if (rightStore.activeWidgetId) rightStore.closeWidget();
          } catch (e) {
            console.warn(
              "Introduction: could not close panels for step",
              stepConfig.element,
              e
            );
            return;
          }
          return waitForIntroLayout();
        }
      });

      instance.onafterchange(() => {
        scheduleIntroRefresh(instance);
      });

      instance.onexit(() => {
        observer.disconnect();
        if (currentIntroInstance === instance) currentIntroInstance = null;
      });

      currentIntroInstance = instance;
      instance.start();
    } catch (e) {
      console.error("Introduction: failed to start intro.js instance:", e);
    }
  }, [selectFirstValues]);

  /**
   * Wait for the map and all intro target elements to be ready, then:
   * - Auto-start the intro (unless suppressed by the user preference).
   * - Expose `window.startIntro` so BottomBar can trigger it manually.
   *
   * Polls every 300 ms because some target elements are mounted lazily.
   * Cleans up timers and any running intro instance on unmount.
   */
  useEffect(() => {
    if (!mapStore?.mapReady) return;

    const prevStart = window.startIntro;
    let cancelled = false;
    let timerId = null;

    const requiredSelectors = INTRO_STEPS.map((step) => step.element).filter(
      Boolean
    );

    const checkElements = () => {
      if (cancelled) return;

      const allPresent = requiredSelectors.every((sel) =>
        document.querySelector(sel)
      );

      if (allPresent) {
        if (!isIntroDismissed()) {
          startIntroInstance();
        }
        window.startIntro = startIntroInstance;
      } else {
        timerId = setTimeout(checkElements, 300);
      }
    };

    timerId = setTimeout(checkElements, 300);

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      window.startIntro = prevStart;
      if (currentIntroInstance) {
        try {
          currentIntroInstance.exit();
        } catch (e) {
          /* ignore */
        }
        currentIntroInstance = null;
      }
    };
  }, [mapStore?.mapReady, startIntroInstance]);

  return <div id="introductionWidget" data-cy="introduction-widget" />;
};

export default observer(Introduction);
